import React, {useContext, useEffect, useRef, useState} from "react"
import {
    DataGrid,
    DataGridDataRowProps,
    DataGridHeaderRowProps,
    IconAndText,
    IconButton,
    Modal,
    SORT_ORDER,
    SortElementProps,
    Text
} from "atmosfair-ui"
import "../styles.css"
import AgenciesCompanyForm, {
    AgenciesCompanyFormProps,
    AgenciesCompanyProps
} from "../../../forms/agencies-company-form/AgenciesCompanyForm";
import {toast} from "react-toastify";
import ToastWithTextOnly from "../../../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {useTranslation} from "react-i18next";
import {
    useCreateAgencyMutation,
    useCreateCompanyMutation,
    useDeleteAgencyMutation,
    useInviteUsersMutation,
    useLazyGetAllAgenciesForKlimaLinkAdminQuery,
    useUpdateAgencyMutation,
    useUpdateCompanyMutation
} from "../../../../redux/reducer/api";
import {AgencyProps, CreateAgencyRequest, SortAgencies, WindowedAgencyProps} from "../../../../api/model/agency";
import {CompanyProps} from "../../../../api/model/company";
import {theme} from "../../../../styles/theme";
import {GENERAL_BACKEND_RESPONSE, ScrollDirection} from "../../../../api/model/common";
import {cloneDeep, isEqual} from "lodash";
import SectionTitle from "../../section-title/SectionTitle";
import {v4 as uuidv4} from "uuid";
import {useAppDispatch, useAppSelector} from "../../../../redux/reducer/store";
import {QueryStatus} from "@reduxjs/toolkit/query";
import {useFusionAuth} from "@fusionauth/react-sdk";
import {
    INITIAL_SELECTED_COUNTRY,
    INITIAL_SELECTED_INTERNATIONAL_PHONE_PREFIX,
    VALIDATE_FORM_STARTING_NUMBER
} from "../../../../constants/default-values";
import {AddressProps} from "../../../forms/address-form/AddressForm";
import {PhoneNumberProps} from "../../../forms/phone-number-form/PhoneNumberForm";
import {ContactPersonProps} from "../../../forms/contact-person-form/ContactPersonForm";
import {updateTravelComponentsOnDeleteAgency} from "../../../../redux/reducer/travel-components";
import {updateJourneyOnDeleteAgency} from "../../../../redux/reducer/journey";
import {isWindowedAgencyProps} from "../../../../utils/check/type-guards";
import SpinnerForKlimaLinkAdmin from "../../common/SpinnerForKlimaLinkAdmin";
import SearchInput from "../../../../molecules/search-input/SearchInput";
import ErrorText from "../../../../molecules/errors/error-text/ErrorText";
import {InstanceContext} from "../../../protected-layout/ProtectedLayout";

interface AgenciesCompanyDataGridProps {
    whichType: AgenciesCompanyFormProps["whichType"]
    company?: CompanyProps,
    agencies?: AgencyProps[],
    allUsers: AgenciesCompanyFormProps['allUsers']
    isCompanyAdmin: boolean,
    isKlimaLinkAdmin: boolean,
    displayInSelectAgencyModal: boolean
    onSelectAgency?: (agency: AgencyProps) => void
    agenciesToHide?: number[]
}

interface AngeciesCompanyDataGridFormProps {
    inviteFirstContact: boolean,
    firstContactFirstName: string,
    firstContactLastName: string,
    firstContactEmail?: string | null,
    firstContactAdmin?: boolean
    inviteSecondContact: boolean,
    secondContactFirstName?: string | null,
    secondContactLastName?: string | null,
    secondContactEmail?: string | null,
    secondContactAdmin?: boolean,
}

export interface OriginalAgencyCompanyProps extends AngeciesCompanyDataGridFormProps, AgenciesCompanyProps, PhoneNumberProps, AddressProps {
}

export interface FormPropsAgency extends AngeciesCompanyDataGridFormProps, AgenciesCompanyProps, PhoneNumberProps, AddressProps {
}

export const initialAgencyFormData: FormPropsAgency = {
    agencyCompanyName: "",
    agencyCompanyEmail: "",
    street: "",
    houseNumber: "",
    zipCode: "",
    city: "",
    additionalAddressInformation: "",
    country: INITIAL_SELECTED_COUNTRY?.name ?? null,
    internationalPhonePrefix: INITIAL_SELECTED_INTERNATIONAL_PHONE_PREFIX,
    phoneNumber: "",
    inviteSecondContact: false,
    inviteFirstContact: false,
    firstContactFirstName: "",
    firstContactLastName: "",
    firstContactEmail: "",
    firstContactAdmin: false,
    secondContactFirstName: "",
    secondContactLastName: "",
    secondContactEmail: '',
    secondContactAdmin: false,
}

interface ContactProps {
    firstName: string,
    lastName: string,
    isAdmin: boolean
    email: string,
}

interface ContactsToInviteProps {
    agencyToInviteTo: string | null,
    firstContact: ContactProps | null
    secondContact: ContactProps | null,
}

const DEFAULT_SORT_VARIABLE = SortAgencies.agencyName
const DEFAULT_SORT_ORDER = SORT_ORDER.ASC

const AgenciesCompanyDataGrid = ({
                                     whichType,
                                     company,
                                     agencies,
                                     isCompanyAdmin,
                                     allUsers,
                                     isKlimaLinkAdmin,
                                     displayInSelectAgencyModal,
                                     onSelectAgency,
                                     agenciesToHide
                                 }: AgenciesCompanyDataGridProps) => {
    const [previousCompanyAgencyName, setPreviousCompanyAgencyName] = useState("")
    const [isLoadingAgencies, setIsLoadingAgencies] = useState(isKlimaLinkAdmin)
    const [contactsToInvite, setContactsToInvite] = useState<ContactsToInviteProps | null>(null)
    const [agencyNameAlreadyGivenError, setAgencyNameAlreadyGivenError] = useState(false)
    const [originalAgencyCompany, setOriginalAgencyCompany] = useState({} as OriginalAgencyCompanyProps)
    const [formData, setFormData] = useState<FormPropsAgency>({...initialAgencyFormData})
    const [sortVariable, setSortVariable] = useState<SortAgencies>(DEFAULT_SORT_VARIABLE)
    const [agenciesCount, setAgenciesCount] = useState<number | null>(null)
    const [companiesCount, setCompaniesCount] = useState<number | null>(null)
    const [sortingOrder, setSortingOrder] = useState<SortElementProps["active"]>(DEFAULT_SORT_ORDER)
    const [headers, setHeaders] = useState<DataGridHeaderRowProps["headers"]>([]);
    const [rows, setRows] = useState<DataGridDataRowProps["rows"]>([])
    const [validateForm, setValidateForm] = useState(VALIDATE_FORM_STARTING_NUMBER)
    const [editAddModalVisible, setEditAddModalVisible] = useState(false)
    const [deleteModalVisible, setDeleteModalVisible] = useState(false)
    const [agencyToUpdate, setAgencyToUpdate] = useState<AgencyProps | null>(null)
    const [agencyToDelete, setAgencyToDelete] = useState<AgencyProps | null>(null)
    const [companyToDelete, setCompanyToDelete] = useState<CompanyProps | null>(null)
    const [companyToUpdate, setCompanyToUpdate] = useState<CompanyProps | null>(null)
    const isEdit = useRef(false)
    const {t, i18n} = useTranslation()
    const {resolvedLanguage} = i18n
    const searchTerm = useRef<string | null>(null)
    const {logout} = useFusionAuth()
    const {disableButtonOnNotProduction} = useContext(InstanceContext);
    const dispatch = useAppDispatch()
    const {
        agencyCompanyName,
        agencyCompanyEmail,
        street,
        houseNumber,
        zipCode,
        city,
        additionalAddressInformation,
        country,
        internationalPhonePrefix,
        phoneNumber,
        firstContactAdmin,
        secondContactAdmin,
        secondContactFirstName,
        secondContactLastName,
        firstContactFirstName,
        firstContactLastName,
        firstContactEmail,
        secondContactEmail,
    } = formData
    const isLoadingGetUsersAgency = useAppSelector(state => {
        return Object.values(state.api.queries).some(query => {
            if (query && query.endpointName === "getUsersAgencies") return query.status === QueryStatus.pending;
        });
    })
    const isLoadingGetUsersCompany = useAppSelector(state => {
        return Object.values(state.api.queries).some(query => {
            if (query && query.endpointName === "getUsersCompany") return query.status === QueryStatus.pending;
        });
    })
    const isErrorGetUsersAgency = useAppSelector(state => {
        return Object.values(state.api.queries).some(query => {
            if (query && query.endpointName === "getUsersAgencies") return query.status === QueryStatus.rejected;
        });
    })
    const isErrorGetUsersCompany = useAppSelector(state => {
        return Object.values(state.api.queries).some(query => {
            if (query && query.endpointName === "getUsersCompany") return query.status === QueryStatus.rejected;
        });
    })
    const nextScrollPosition = useRef<string | null>(null)
    const isDataFinished = useRef<boolean>(false)
    const [getAllAgenciesForKlimaLinkAdmin, {
        isError: isErrorGetAllAgenciesForKlimaLinkAdmin
    }] = useLazyGetAllAgenciesForKlimaLinkAdminQuery()
    const [inviteUsersRequest, {
        isError: isErrorInviteUser
    }] = useInviteUsersMutation()

    const [createCompany, {
        data: createCompanyResponse,
        isLoading: isLoadingCreateCompanyRequest,
    }] = useCreateCompanyMutation()

    const [createAgency, {
        data: createAgencyResponse,
        isLoading: isLoadingCreateAgencyRequest,
    }] = useCreateAgencyMutation()

    const [updateAgency, {
        data: updateAgencyResponse,
        isLoading: isLoadingUpdateAgencyRequest,
    }] = useUpdateAgencyMutation()

    const [updateCompany, {
        data: updateCompanyResponse,
        isLoading: isLoadingUpdateCompanyRequest,
    }] = useUpdateCompanyMutation()

    const [deleteAgency, {
        data: deleteAgencyResponse,
        isLoading: isLoadingDeleteAgencyRequest,
        isError: isErrorDeleteAgencyRequest
    }] = useDeleteAgencyMutation()

    useEffect(() => {
        if (displayInSelectAgencyModal && whichType !== "agencies") throw new Error("When setting displayInSelectAgencyModal to true, you must set whichType to agencies.")
        if (displayInSelectAgencyModal && typeof onSelectAgency === 'undefined') throw new Error("When setting displayInSelectAgencyModal to true, you must provide a onSelectAgency function.")
        if (displayInSelectAgencyModal && typeof agenciesToHide === 'undefined') throw new Error("When setting displayInSelectAgencyModal to true, you must provide agenciesToHide.")
    }, []);

    const _deletingOwnAgency = () => agencies?.filter(agency => agency.isAdmin).some(agency => agency.id === agencyToDelete?.id)

    useEffect(() => {
        if (deleteAgencyResponse?.status === GENERAL_BACKEND_RESPONSE.success) {
            if (!isCompanyAdmin && _deletingOwnAgency()) logout()
            if (agencyToDelete !== null) {
                dispatch(updateTravelComponentsOnDeleteAgency(agencyToDelete))
                dispatch(updateJourneyOnDeleteAgency(agencyToDelete))
            }
            setAgencyToDelete(null)
        }
    }, [deleteAgencyResponse]);

    useEffect(() => {
        if (isErrorDeleteAgencyRequest) setAgencyToDelete(null)
    }, [isErrorDeleteAgencyRequest]);

    useEffect(() => {
        if (isErrorGetUsersAgency) toast(<ToastWithTextOnly
                message={"ERRORS.GENERAL_REFETCHING"}/>,
            {progress: undefined, type: "error"}
        )
    }, [isErrorGetUsersAgency, isErrorGetUsersCompany]);

    useEffect(() => {
        if (
            updateAgencyResponse?.status === GENERAL_BACKEND_RESPONSE.success ||
            updateCompanyResponse?.status === GENERAL_BACKEND_RESPONSE.success
        ) {
            toast(<ToastWithTextOnly
                    message={"TOASTS.SUCCESS.EDIT_COMPANY"}/>,
                {progress: undefined, type: "success"}
            )
            setEditAddModalVisible(false)
        }
    }, [updateAgencyResponse, updateCompanyResponse])

    useEffect(() => {
        if (isErrorInviteUser) toast(<ToastWithTextOnly
                message={"SCREENS.SETTINGS.INVITE_CONTACT_PERSON_FAIL"}/>,
            {progress: undefined, type: "error"}
        );
    }, [isErrorInviteUser]);

    useEffect(() => {
        if (contactsToInvite && agencies && agencies.length > 0) {
            const {agencyToInviteTo} = contactsToInvite
            const newlyCreatedAgency = agencies.find(agency => agency.name === agencyToInviteTo)
            const {firstContact, secondContact} = contactsToInvite
            if (newlyCreatedAgency) {
                const {id} = newlyCreatedAgency
                if (firstContact) _inviteUser(firstContact, id)
                if (secondContact) _inviteUser(secondContact, id)
                _resetCreateOrUpdateForm()
            }
        }
    }, [agencies]);

    useEffect(() => {
        if (
            createCompanyResponse?.status === GENERAL_BACKEND_RESPONSE.success ||
            createAgencyResponse?.status === GENERAL_BACKEND_RESPONSE.success
        ) {
            setEditAddModalVisible(false)
            if (!contactsToInvite) _resetCreateOrUpdateForm()
            toast(<ToastWithTextOnly
                    message={`TOASTS.SUCCESS.${whichType === "company" ? "ADD_COMPANY" : "ADD_AGENCY"}`}/>,
                {progress: undefined, type: "success"}
            );
        }
    }, [createCompanyResponse, createAgencyResponse])

    useEffect(() => {
        const fetchData = async () => {
            _resetOnFetch()
            if (isKlimaLinkAdmin) {
                const newRows = await _fetchRows()
                setIsLoadingAgencies(false)
                setRows(newRows)
            } else {
                if (typeof agencies !== "undefined") setRows(_toAgencyDataGrid(agencies))
            }
        };

        fetchData().catch((error) => {
            console.error("Error fetching data:", error);
        });
    }, [isKlimaLinkAdmin, sortingOrder, sortVariable, resolvedLanguage, agencies]);

    useEffect(() => {
        setHeaders(_setHeaders())
    }, [sortingOrder, sortVariable])

    const _resetOnFetch = () => {
        nextScrollPosition.current = null
        isDataFinished.current = false
        setRows([])
    }

    const _setHeaders = () => {
        if (isKlimaLinkAdmin) return [
            {
                text: "GENERAL.AGENCY",
                sort: {
                    onCaretDown: () => {
                        if (sortVariable !== SortAgencies.agencyName || sortingOrder !== SORT_ORDER.ASC) {
                            setSortVariable(SortAgencies.agencyName);
                            setSortingOrder(SORT_ORDER.ASC);
                            _resetOnFetch()
                        }
                    },
                    onCaretUp: () => {
                        if (sortVariable !== SortAgencies.agencyName || sortingOrder !== SORT_ORDER.DESC) {
                            setSortVariable(SortAgencies.agencyName);
                            setSortingOrder(SORT_ORDER.DESC)
                            _resetOnFetch()
                        }
                    },
                    disabled: false,
                },
            },
            {
                text: "GENERAL.COMPANY",
                sort: {
                    onCaretDown: () => {
                        if (sortVariable !== SortAgencies.companyName || sortingOrder !== SORT_ORDER.ASC) {
                            setSortVariable(SortAgencies.companyName);
                            setSortingOrder(SORT_ORDER.ASC);
                            _resetOnFetch()
                        }
                    },
                    onCaretUp: () => {
                        if (sortVariable !== SortAgencies.companyName || sortingOrder !== SORT_ORDER.DESC) {
                            setSortVariable(SortAgencies.companyName);
                            setSortingOrder(SORT_ORDER.DESC)
                            _resetOnFetch()
                        }
                    },
                    disabled: false,
                },
            }
        ]

        return [
            {text: "GENERAL.AGENCY"},
            {text: "SCREENS.SETTINGS.USERS_COUNT"}
        ]
    }

    const _toAgencyDataGrid = (agencies: AgencyProps[] | WindowedAgencyProps[]) => {
        const agencyDataGridRows: DataGridDataRowProps["rows"] = []
        if (agencies) {
            let filteredAgencies = [...agencies]
            if (typeof agenciesToHide !== "undefined" && isKlimaLinkAdmin) filteredAgencies = filteredAgencies.filter((agency: AgencyProps | WindowedAgencyProps) =>
                !agenciesToHide.includes(agency.id)
            )
            filteredAgencies.forEach(agency => {
                const members = allUsers.filter(user => user.agencyIds.includes(agency.id))
                let companyName = ''
                if (isWindowedAgencyProps(agency)) companyName = agency.companyName
                const {name: agencyName} = agency
                const membersLength = members.length
                agencyDataGridRows.push({
                    data: [
                        {
                            text: agencyName!!,
                            value: agencyName!!,
                        },
                        ...(isKlimaLinkAdmin
                            ? [
                                {
                                    text: companyName,
                                    value: companyName,
                                },
                            ]
                            : []),
                        ...(!isKlimaLinkAdmin ? [{
                            text: membersLength.toString(),
                            value: membersLength
                        }] : []),
                    ],
                    rawData: {...agency} as AgencyProps,
                    key: uuidv4(),
                    actions: [
                        {
                            hide: displayInSelectAgencyModal,
                            disabled: disableButtonOnNotProduction,
                            icon: "penToSquare",
                            tooltip: _setEditDeleteTooltip("EDIT"),
                            onClick: (_, value) => _onEditCompanyAgency(value)
                        },
                        {
                            hide: displayInSelectAgencyModal,
                            disabled: disableButtonOnNotProduction,
                            icon: "trash",
                            tooltip: _setEditDeleteTooltip("DELETE"),
                            onClick: (_, value) => {
                                setDeleteModalVisible(true)
                                setAgencyToDelete(value)
                            }
                        },
                    ]
                })
            })
        }
        return agencyDataGridRows
    }

    const _fetchRows = async (): Promise<DataGridDataRowProps["rows"]> => {
        let newRows: DataGridDataRowProps["rows"] = [];
        if (!isDataFinished.current) {
            try {
                const data = await getAllAgenciesForKlimaLinkAdmin({
                    scrollPosition: nextScrollPosition.current,
                    size: 10,
                    direction: ScrollDirection.forward,
                    sort: sortVariable,
                    sortOrder: sortingOrder,
                    searchTerm: searchTerm.current,
                }).unwrap();
                nextScrollPosition.current = (data?.nextPosition ?? null);
                if (data?.nextPosition === null || data?.nextPosition === "null") isDataFinished.current = true
                setAgenciesCount(data.agenciesCount)
                setCompaniesCount(data.companiesCount)
                newRows = _toAgencyDataGrid(data.agencies)
                if (isDataFinished.current && searchTerm === null) {
                    const updatedRows = cloneDeep(rows) as DataGridDataRowProps["rows"]
                    newRows.forEach(newRow => updatedRows.push(newRow))
                    newRows = updatedRows
                }
            } catch (error) {
                console.warn("Something went wrong getting agencies: ", error)
            }
        } else {
            return newRows
        }
        return newRows
    }

    const _inviteUser = (userToInvite: ContactProps, agencyId: number) => {
        const {email, firstName, lastName, isAdmin} = userToInvite
        if (Boolean(allUsers.find(user => user.email === email))) {
            toast(<ToastWithTextOnly
                    message={t("SCREENS.SETTINGS.ADD_USER_EMAIL_ALREADY_IN_USE", {email})}/>,
                {progress: undefined, type: "error"}
            );
        } else {
            inviteUsersRequest({
                users: [{
                    firstName,
                    lastName,
                    email,
                    isAdmin,
                    language: resolvedLanguage ?? "de",
                }],
                agencyId
            })
        }
    }

    const _createCompany = (firstContactPersonData: ContactPersonProps, secondContactPersonData: ContactPersonProps, addressData: AddressProps, phoneData: PhoneNumberProps, formData: AgenciesCompanyProps) => {
        const {street, houseNumber, zipCode, city, country, additionalAddressInformation} = addressData
        const {phoneNumber, internationalPhonePrefix} = phoneData
        const {
            agencyCompanyName,
            agencyCompanyEmail,
        } = formData
        return createCompany({
            name: agencyCompanyName,
            address: {
                street,
                houseNumber,
                zipCode,
                city,
                country: country!!,
                additionalInformation: additionalAddressInformation,
                phoneNumber,
                internationalPhonePrefix: internationalPhonePrefix!!,
            },
            generalEmail: agencyCompanyEmail,
            firstContactFirstName: firstContactPersonData.firstName,
            firstContactLastName: firstContactPersonData.lastName,
            firstContactEmail: firstContactPersonData.email,
            secondContactFirstName: secondContactPersonData.firstName,
            secondContactLastName: secondContactPersonData.lastName,
            secondContactEmail: secondContactPersonData.email
        })
    }

    const _renderAddIcon = () => <div style={{width: '200px'}}>
        <IconAndText
            disabled={disableButtonOnNotProduction}
            tooltip={disableButtonOnNotProduction ? "TOOLTIP.SETTINGS.CREATE_AGENCIES_ON_STAGING" : undefined}
            iconColor={theme.colors.primary}
            text={`SCREENS.SETTINGS.${whichType === "agencies" ? "ADD_NEW_AGENCY" : "ADD_NEW_COMPANY"}`}
            onClick={() => setEditAddModalVisible(true)}
            icon={"plus"}/>
    </div>

    const _renderAddButton = () => {
        if (whichType === "agencies" || !company) return _renderAddIcon()
        return null
    }

    const _onEditCompanyAgency = (value: AgencyProps | CompanyProps) => {
        isEdit.current = true
        const {
            name,
            firstContactFirstName,
            firstContactLastName,
            firstContactEmail,
            secondContactFirstName,
            secondContactLastName,
            secondContactEmail,
            address,
            generalEmail,
        } = value
        const {
            street,
            houseNumber,
            zipCode,
            city,
            additionalInformation,
            country,
            internationalPhonePrefix,
            phoneNumber,
        } = address
        setOriginalAgencyCompany({
            agencyCompanyName,
            firstContactFirstName,
            firstContactLastName,
            secondContactFirstName: secondContactFirstName ?? "",
            secondContactLastName: secondContactLastName ?? "",
            firstContactEmail: firstContactEmail ?? "",
            secondContactEmail: secondContactEmail ?? "",
            firstContactAdmin: Boolean(allUsers.find(user => user.email === "firstContactEmail")),
            secondContactAdmin: Boolean(allUsers.find(user => user.email === 'secondContactEmail')),
            agencyCompanyEmail,
            street,
            houseNumber,
            zipCode,
            city,
            additionalAddressInformation: additionalInformation,
            country,
            internationalPhonePrefix,
            phoneNumber,
            inviteFirstContact: false,
            inviteSecondContact: false,
        })
        setPreviousCompanyAgencyName(name)
        setFormData({
            agencyCompanyName: name,
            street,
            houseNumber,
            zipCode,
            city,
            additionalAddressInformation: additionalInformation ?? "",
            agencyCompanyEmail: generalEmail,
            country,
            internationalPhonePrefix,
            phoneNumber,
            inviteFirstContact: false,
            inviteSecondContact: false,
            firstContactFirstName,
            firstContactLastName,
            secondContactFirstName: secondContactFirstName ?? "",
            secondContactLastName: secondContactLastName ?? "",
            firstContactEmail: firstContactEmail ?? "",
            secondContactEmail: secondContactEmail ?? "",
        })
        setEditAddModalVisible(true)
        if (whichType === "agencies") setAgencyToUpdate(value as AgencyProps)
        if (whichType === "company") setCompanyToUpdate(value as CompanyProps)
    }

    const _createAgencyRequestObject = (firstContactPersonData: ContactPersonProps, secondContactPersonData: ContactPersonProps, addressData: AddressProps, phoneData: PhoneNumberProps, formData: AgenciesCompanyProps): CreateAgencyRequest => {
        const {street, houseNumber, zipCode, city, country, additionalAddressInformation} = addressData
        const {phoneNumber, internationalPhonePrefix} = phoneData
        const {
            agencyCompanyName,
            agencyCompanyEmail,
        } = formData
        return {
            name: agencyCompanyName,
            address: {
                street,
                houseNumber,
                zipCode,
                city,
                country: country!!,
                additionalInformation: additionalAddressInformation,
                phoneNumber,
                internationalPhonePrefix: internationalPhonePrefix!!,
            },
            generalEmail: agencyCompanyEmail,
            firstContactFirstName: firstContactPersonData.firstName,
            firstContactLastName: firstContactPersonData.lastName,
            firstContactEmail: firstContactPersonData.email,
            secondContactFirstName: secondContactPersonData.firstName,
            secondContactLastName: secondContactPersonData.lastName,
            secondContactEmail: secondContactPersonData.email
        }
    }

    const _updateAgency = (firstContactPersonData: ContactPersonProps, secondContactPersonData: ContactPersonProps, addressData: AddressProps, phoneData: PhoneNumberProps, formData: AgenciesCompanyProps) => {
        if (agencyToUpdate) {
            const {id} = agencyToUpdate
            const updatedAgencyObject = _createAgencyRequestObject(firstContactPersonData, secondContactPersonData, addressData, phoneData, formData)
            if (!isEqual(originalAgencyCompany, updatedAgencyObject)) updateAgency({
                agency: updatedAgencyObject,
                agencyId: id,
            })
        }
    }

    const _updateCompany = (addressData: AddressProps, phoneData: PhoneNumberProps, formData: AgenciesCompanyProps) => {
        if (companyToUpdate) {
            const {street, houseNumber, zipCode, city, country, additionalAddressInformation} = addressData
            const {phoneNumber, internationalPhonePrefix} = phoneData
            const {agencyCompanyName, agencyCompanyEmail} = formData
            const updatedCompanyObject = {
                name: agencyCompanyName,
                address: {
                    street: street,
                    houseNumber: houseNumber,
                    zipCode: zipCode,
                    city: city,
                    country: country!!,
                    additionalInformation: additionalAddressInformation,
                    phoneNumber: phoneNumber,
                    internationalPhonePrefix: internationalPhonePrefix ?? undefined!!,
                },
                generalEmail: agencyCompanyEmail,
                firstContactFirstName: firstContactFirstName,
                firstContactLastName: firstContactLastName,
                firstContactEmail: firstContactEmail ? firstContactEmail : null,
                secondContactFirstName: secondContactFirstName ? secondContactFirstName : null,
                secondContactLastName: secondContactLastName ? secondContactLastName : null,
                secondContactEmail: secondContactEmail ? secondContactEmail : null
            }
            if (!isEqual(updatedCompanyObject, originalAgencyCompany)) updateCompany(updatedCompanyObject)
        }
    }

    const _resetCreateOrUpdateForm = () => {
        isEdit.current = false
        setFormData(initialAgencyFormData)
        setAgencyNameAlreadyGivenError(false)
        setCompanyToUpdate(null)
        setAgencyToUpdate(null)
        setContactsToInvite(null)
        setPreviousCompanyAgencyName("")
        setOriginalAgencyCompany({} as OriginalAgencyCompanyProps)
    }

    const _agencyNameAlreadyGiven = (newAgencyName: string) => agencies?.map(agency => agency.name).includes(newAgencyName) && newAgencyName !== previousCompanyAgencyName

    const _showAgencyNameAlreadyGivenError = (agencyName: string) => toast(<ToastWithTextOnly
            message={t('SCREENS.SETTINGS.AGENCY_NAME_ALREADY_GIVEN',
                {agencyName, interpolation: {escapeValue: false}},
            )}/>,
        {progress: undefined, type: "error"}
    );

    const _createOrUpdateCompanyOrAgency = (firstContactPersonData: ContactPersonProps, secondContactPersonData: ContactPersonProps, addressData: AddressProps, phoneData: PhoneNumberProps, formData: AgenciesCompanyProps) => {
        const {agencyCompanyName} = formData
        const newAgencyName = agencyCompanyName
        const duplicatedName = Boolean(_agencyNameAlreadyGiven(newAgencyName))
        setAgencyNameAlreadyGivenError(duplicatedName)
        if (duplicatedName) _showAgencyNameAlreadyGivenError(newAgencyName)
        if (agencyToUpdate && !duplicatedName) {
            _updateAgency(firstContactPersonData, secondContactPersonData, addressData, phoneData, formData)
            setValidateForm(VALIDATE_FORM_STARTING_NUMBER)
        } else if (companyToUpdate) {
            _updateCompany(addressData, phoneData, formData)
            setValidateForm(VALIDATE_FORM_STARTING_NUMBER)
        } else {
            if (whichType === "agencies" && !duplicatedName) {
                createAgency(_createAgencyRequestObject(firstContactPersonData, secondContactPersonData, addressData, phoneData, formData))
                setValidateForm(VALIDATE_FORM_STARTING_NUMBER)
            }
            if (whichType === "company") {
                _createCompany(firstContactPersonData, secondContactPersonData, addressData, phoneData, formData)
                setValidateForm(VALIDATE_FORM_STARTING_NUMBER)
            }
            let firstContact: null | ContactProps = null
            let secondContact: null | ContactProps = null
            if (firstContactPersonData.inviteContact) firstContact = {
                isAdmin: Boolean(firstContactPersonData.isAdmin),
                firstName: firstContactPersonData.firstName,
                lastName: firstContactPersonData.lastName,
                email: firstContactPersonData.email,
            }

            if (secondContactPersonData.inviteContact) secondContact = {
                isAdmin: Boolean(secondContactPersonData.isAdmin),
                firstName: secondContactPersonData.firstName,
                lastName: secondContactPersonData.lastName,
                email: secondContactPersonData.email,
            }
            setContactsToInvite({
                agencyToInviteTo: agencyCompanyName,
                firstContact,
                secondContact
            })
        }
    }

    const _onCloseDeleteModal = () => {
        setAgencyToDelete(null)
        setCompanyToDelete(null)
        setDeleteModalVisible(false)
    }

    const _onDeleteCompanyAgency = () => {
        if (whichType === "agencies" && agencyToDelete) {
            deleteAgency(agencyToDelete.id)
            setDeleteModalVisible(false)
        }
        // if (whichType === "company") {
        // setCompanyToDelete(null)
        // setDeleteModalVisible(false)
    }

    const _onCloseEditAddModal = () => {
        setEditAddModalVisible(false)
        setValidateForm(VALIDATE_FORM_STARTING_NUMBER)
        _resetCreateOrUpdateForm()
    }

    const _setEditDeleteTooltip = (tooltip: "EDIT" | "DELETE") => {
        if (disableButtonOnNotProduction) return "TOOLTIP.COMMON.DISABLED_FUNCTIONALITY_DEV_INSTANCE"
        if (displayInSelectAgencyModal) return undefined
        return `TOOLTIP.SETTINGS.${whichType === "agencies" ? `${tooltip}_AGENCY` : `${tooltip}_COMPANY`}`
    }


    const _renderDataGrid = () => {
        const AgenciesCompaniesDataGrid = <DataGrid
            look={"primaryLight"}
            cellWeights={headers.map(_ => 2)}
            headers={headers}
            rows={rows}
            rowSize={"medium"}
            hover={displayInSelectAgencyModal}
            onClickRow={(_, agency) => onSelectAgency && onSelectAgency(agency as AgencyProps)}
            {...(isKlimaLinkAdmin ? {
                fetchRows: () => _fetchRows()
            } : {})}/>
        if (isLoadingAgencies) return <SpinnerForKlimaLinkAdmin/>
        if (isErrorGetAllAgenciesForKlimaLinkAdmin) return <ErrorText>ERRORS.GENERAL_NETWORK_ERROR_BODY</ErrorText>
        if (isKlimaLinkAdmin) return AgenciesCompaniesDataGrid
        if (agencies && agencies.length > 0) return AgenciesCompaniesDataGrid
        return null
    }

    const _onChange = async (query: string) => {
        nextScrollPosition.current = null
        isDataFinished.current = false
        const queryForSearch = query.length > 0 ? query : null
        searchTerm.current = queryForSearch
        const newRows = await _fetchRows()
        setRows(newRows)
    }

    return <section>
        {!displayInSelectAgencyModal && <SectionTitle
            isLoading={
                (whichType === "agencies" && isLoadingCreateAgencyRequest) ||
                (whichType === "company" && isLoadingCreateCompanyRequest) ||
                (whichType === "agencies" && isLoadingUpdateAgencyRequest) ||
                (whichType === "company" && isLoadingUpdateCompanyRequest) ||
                (whichType === "agencies" && isLoadingGetUsersAgency) ||
                (whichType === "company" && isLoadingGetUsersCompany) ||
                (whichType === "agencies" && isLoadingDeleteAgencyRequest)
            }
            title={whichType === "agencies" ? "SCREENS.SETTINGS.AGENCIES_TITLE" : "SCREENS.SETTINGS.COMPANY_TITLE"}
            subTitle={(isKlimaLinkAdmin && agenciesCount !== null && companiesCount !== null) ? `${t("GENERAL.TOTAL")}: ${agenciesCount} ${t("GENERAL.AGENCIES")},
            ${companiesCount} ${t("GENERAL.COMPANIES")}` : undefined}
        />}
        <div
            className={"agencies-company-wrapper"}>
            {!displayInSelectAgencyModal && !isKlimaLinkAdmin && _renderAddButton()}
            {company && <div className={"agencies-company-company-wrapper"}>
                <Text>{company.name}</Text>
                <div className={"agencies-company-company-button-wrapper"}>
                    {isCompanyAdmin && !isKlimaLinkAdmin && <IconButton
                        tooltip={disableButtonOnNotProduction ? "TOOLTIP.COMON.DISABLED_FUNCTIONALITY_DEV_INSTANCE" : `TOOLTIP.SETTINGS.${whichType === "agencies" ? "EDIT_AGENCY" : "EDIT_COMPANY"}`}
                        size={"small"}
                        color={"primary"}
                        disabled={disableButtonOnNotProduction}
                        onClick={() => _onEditCompanyAgency(company)}
                        icon={"penToSquare"}/>}
                    {/*{isCompanyAdmin && <IconButton*/}
                    {/*    tooltip={disableButtonOnNotProduction ? "TOOLTIP.COMMON.DISABLED_FUNCTIONALITY_DEV_INSTANCE" : `TOOLTIP.SETTINGS.${whichType === "agencies" ? "DELETE_AGENCY" : "DELETE_COMPANY"}`}*/}
                    {/*    size={"small"}*/}
                    {/*    disabled={disableButtonOnNotProduction}*/}
                    {/*    color={"primary"}*/}
                    {/*    onClick={() => _onDeleteCompanyAgency(company)}*/}
                    {/*    icon={"trash"}/>}*/}
                </div>
            </div>}
            {isKlimaLinkAdmin && whichType === "agencies" && <div style={{width: '100%'}}>
                <SearchInput
                    label={"SCREENS.SETTINGS.SEARCH_BAR_LABEL_AGENCIES"}
                    placeholder={"GENERAL.SEARCH"}
                    onChange={_onChange}
                />
            </div>}
            {whichType === "agencies" && _renderDataGrid()}

        </div>
        <Modal
            headline={(agencyToUpdate || companyToUpdate) ?
                `SCREENS.SETTINGS.${whichType === "agencies" ? "EDIT_AGENCY" : "EDIT_COMPANY"}` :
                `SCREENS.SETTINGS.${whichType === "agencies" ? "ADD_NEW_AGENCY" : "ADD_NEW_COMPANY"}`
            }
            show={editAddModalVisible}
            closeButtonText={"GENERAL.ABORT"}
            confirmButtonText={"GENERAL.OK"}
            onConfirm={() => setValidateForm(validateForm + 1)}
            onBackdropClick={_onCloseEditAddModal}
            onClose={_onCloseEditAddModal}
            showSpinner={isLoadingUpdateAgencyRequest || isLoadingCreateAgencyRequest}
            showPaper={true}>
            <AgenciesCompanyForm
                onEnterKeyPressed={() => setValidateForm(validateForm + 1)}
                nameAlreadyGivenError={agencyNameAlreadyGivenError}
                showMakeAdminCheckboxes={true}
                allUsers={allUsers}
                isEdit={isEdit.current}
                whichType={whichType}
                validateForm={validateForm}
                onFormIsValid={_createOrUpdateCompanyOrAgency}
                defaultValues={{
                    country,
                    street,
                    houseNumber,
                    zipCode,
                    city,
                    additionalAddressInformation,
                    internationalPhonePrefix,
                    phoneNumber,
                    agencyCompanyEmail,
                    agencyCompanyName,
                    firstContactPerson: {
                        email: firstContactEmail ?? "",
                        firstName: firstContactFirstName,
                        lastName: firstContactLastName
                    },
                    secondContactPerson: {
                        firstName: secondContactFirstName ?? "",
                        lastName: secondContactLastName ?? "",
                        email: secondContactEmail ?? ""
                    }
                }}/>
        </Modal>
        <Modal
            headline={t(`SCREENS.SETTINGS.${whichType === "agencies" ? "DELETE_AGENCY" : "DELETE_COMPANY"}`, {
                name: whichType === "agencies" ? agencyToDelete?.name : companyToDelete?.name,
                interpolation: {escapeValue: false}
            })}
            onEnterKeyPressed={_onDeleteCompanyAgency}
            show={deleteModalVisible}
            closeButtonText={"GENERAL.ABORT"}
            confirmButtonText={"GENERAL.DELETE"}
            confirmButtonColor={"warningMain"}
            onConfirm={_onDeleteCompanyAgency}
            onBackdropClick={_onCloseDeleteModal}
            onClose={_onCloseDeleteModal}
            showSpinner={isLoadingDeleteAgencyRequest}
            showPaper={true}>
            <Text>{t(
                `SCREENS.SETTINGS.${whichType === "agencies" ? 'DELETE_AGENCY_TEXT' : 'DELETE_COMPANY_TEXT'}`,
                (whichType === "agencies" ?
                    {
                        agency: agencyToDelete?.name,
                        interpolation: {escapeValue: false}
                    } :
                    {
                        company: companyToDelete?.name,
                        interpolation: {escapeValue: false}
                    }))
            }
            </Text>
            {!isCompanyAdmin && _deletingOwnAgency() && <Text>SCREENS.SETTINGS.DELETE_OWN_AGENCY_TEXT</Text>}
        </Modal>
    </section>
}

export default AgenciesCompanyDataGrid